/**
 * DM Curated First — universal build
 *
 * Pins one product to position #1 of a collection grid from a URL parameter, so
 * a Meta/Facebook catalog ad lands the shopper on a collection with the exact
 * product they clicked already first.
 *
 *   /collections/best-sellers?first=<handle>
 *   /collections/best-sellers?first=<handle>&variant=<id>
 *   /collections/best-sellers?first={{product.link}}      (Meta catalog macro)
 *
 * ---------------------------------------------------------------------------
 * WHY THIS BUILD IS THEME-AGNOSTIC
 *
 * The Debutify build hardcoded #ProductGridContainer / ul#product-grid /
 * li.grid__item. Those exist in Dawn and its forks and nowhere else — Horizon
 * uses <results-list>, [ref="grid"] and li[ref="cards[]"], Prestige uses
 * .product-list / .product-item, and so on. Selector lists per theme do not
 * scale past about three themes and fail silently on the fourth.
 *
 * So this build does not look for named elements. It finds the grid
 * STRUCTURALLY: the element with the most direct children that each contain a
 * product link is the grid, and those children are the cards. That definition
 * holds on any theme that renders a collection as a repeated list, which is all
 * of them, and it keeps working when a theme renames its classes.
 *
 * Explicit selectors (data-grid / data-card / data-container) always win when
 * supplied. Auto-detection is the default, not the only option.
 *
 * ---------------------------------------------------------------------------
 * FETCH MODES — what happens when the product is NOT in the visited grid
 *
 * This is the case that needs theme-specific markup, because we have to build a
 * card that matches its neighbours. Three modes, in descending fidelity:
 *
 *   view    (default)  GET /products/{handle}?view={view}
 *                      An alternate product template carrying {% layout none %}
 *                      that renders the theme's own card snippet. Portable: it
 *                      needs no section, no block config and no template JSON.
 *                      Debutify itself ships four of these
 *                      (product.internal-upsell-item.liquid and friends), so
 *                      the pattern is not exotic.
 *
 *   section            GET /products/{handle}?section_id={id}
 *                      Section Rendering API. Equivalent fidelity. Use where a
 *                      section is preferred, or where a theme's card snippet
 *                      needs section.settings to render correctly.
 *
 *   clone              No request for markup at all. Deep-clones a sibling card
 *                      and rewrites it from /products/{handle}.js. Works on ANY
 *                      theme with zero Liquid, at the cost of fidelity: all
 *                      interactive controls are stripped (see rewriteClone) and
 *                      only image, title, price and links survive.
 *                      This is the safety net, not the target.
 *
 * In every mode the card WRAPPER is built by cloning a real sibling's shell, so
 * the outer element always has the right tag and classes for the grid it is
 * being inserted into. The template only has to produce card contents.
 *
 * ---------------------------------------------------------------------------
 * Config arrives on the loader <script> tag's data-* attributes.
 * Nothing is written to window. The only global read is Shopify.routes.root.
 */

// ---- module constants -------------------------------------------------------
//
// These MUST be declared above the entry point below. Function declarations are
// hoisted, but `const` is not — it sits in the temporal dead zone until its
// line is evaluated. The entry point calls init() -> detectGrid(), which reads
// PRODUCT_LINK, so declaring these further down (next to the functions that use
// them, which reads better) throws
//   ReferenceError: Cannot access 'PRODUCT_LINK' before initialization
// on every page load. Caught by the test suite, not by review.

/** Every product link on a storefront, whatever the theme calls its cards. */
const PRODUCT_LINK = 'a[href*="/products/"]';

/**
 * Regions whose product links are never part of the collection grid.
 * Header mega-menus and cart drawers are full of /products/ hrefs and would
 * otherwise compete with the real grid during detection.
 */
const EXCLUDED =
  'header, nav, footer, [role="dialog"], dialog, form[action*="/cart"], ' +
  '[class*="drawer"], [class*="modal"], [class*="popup"], [class*="mini-cart"], ' +
  '[class*="cart-drawer"], [class*="predictive"], [class*="mega-menu"], [id*="Cart"]';

/** Classes themes use to gate card visibility until an element scrolls in. */
const ANIMATION_CLASSES = [
  'scroll-trigger', 'scroll-trigger--offscreen', 'scroll-trigger--cancel', // Dawn / Debutify
  'animate--fade-in', 'animate--slide-in', 'animate--zoom-in',
  'reveal', 'reveal-visible', 'aos-init', 'aos-animate', // AOS / Out of the Sandbox
  'lazyload', 'fade-in', 'is-hidden', 'not-visible',
];

/**
 * Content bound to the product it was rendered for, and therefore WRONG on a
 * clone of a different product. Removed rather than rewritten.
 *
 * The forms matter for correctness, not tidiness: a cloned quick-add form
 * carries the ORIGINAL product's variant id, so leaving it in place gives the
 * shopper a button that adds a different product to their cart. Everything else
 * here is removed because it cannot be derived from /products/{handle}.js
 * reliably enough to be worth guessing.
 */
const CLONE_STRIP = [
  'form', 'product-form', 'quick-add-bulk', 'variant-selects', 'card-product-variant-selects',
  '[class*="quick-add"]', '[class*="quick-view"]', '[class*="quick-shop"]',
  '[class*="swatch"]', '[class*="badge"]', '[class*="rating"]', '[class*="review"]',
  '[class*="wishlist"]', '[class*="compare"]', '[class*="countdown"]',
];

/** Optional card content governed by a collection-level setting, not the product. */
const OPTIONAL_BLOCKS = [
  'card-product-variant-selects, variant-selects, [class*="swatch"]',
  '[class*="quick-add"], [class*="quick-view"], [class*="quick-shop"]',
  '[class*="card__description"], [class*="product-description"]',
  '[class*="rating"], [class*="review-stars"]',
  '[class*="vendor"]',
];

// ---- entry point -------------------------------------------------------------

const cfg = readConfig();

if (cfg.enabled && cfg.mode !== 'off') {
  const params = new URLSearchParams(window.location.search);
  const firstParam = params.get(cfg.param);
  if (firstParam) {
    const parsed = parseFirstParam(firstParam, params.get(cfg.variantParam));
    if (parsed.handle) init(parsed);
  }
}

// ---- config ---------------------------------------------------------------

function readConfig() {
  const el = document.querySelector('script[data-dm-curated-first]');
  const d = (el && el.dataset) || {};
  return {
    enabled: el != null,
    mode: d.mode || 'view',
    param: d.param || 'first',
    variantParam: d.variantParam || 'variant',
    view: d.view || 'dm-card',
    cardSection: d.cardSection || 'dm-curated-card',
    swapImage: d.swapImage !== 'false',
    containerSelector: d.container || '',
    gridSelector: d.grid || '',
    cardSelector: d.card || '',
    stripSelector: d.strip || '',
    minCards: parseInt(d.minCards, 10) || 2,
    debug: d.debug === 'true',
  };
}

function log() {
  if (!cfg.debug || !window.console) return;
  console.log.apply(console, ['[dm-curated-first]'].concat([].slice.call(arguments)));
}

function warn(msg) {
  if (window.console) console.warn('[dm-curated-first] ' + msg);
}

// ---- storefront routing ---------------------------------------------------

function shopifyRoot() {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.charAt(root.length - 1) === '/' ? root : root + '/';
}

// ---- param parsing --------------------------------------------------------

/**
 * `first` may be a bare handle or a full/encoded product URL — Meta's catalog
 * macro emits the latter.
 *
 * URLSearchParams#get() already percent-decodes once, so the value is normally
 * plain text. Some ad-link generators double-encode, so we decode ONE more time
 * — but only when the value does not already read as a URL, and only act on the
 * result if that decode reveals one. Unconditional decoding throws on a handle
 * containing a literal "%" (e.g. "50%-off-tee"), hence the try/catch.
 */
function parseFirstParam(firstParam, fallbackVariant) {
  let handle = firstParam;
  let variant = fallbackVariant || null;
  let candidate = firstParam;

  if (!/^https?:\/\//.test(candidate)) {
    try {
      const decoded = decodeURIComponent(candidate);
      if (/^https?:\/\//.test(decoded)) candidate = decoded;
    } catch (e) {
      /* not valid percent-encoding — treat as a plain handle */
    }
  }

  if (/^https?:\/\//.test(candidate)) {
    try {
      const url = new URL(candidate);
      const parts = url.pathname.split('/');
      const idx = parts.indexOf('products');
      handle = idx !== -1 && parts[idx + 1] ? parts[idx + 1] : '';
      if (!variant) variant = new URLSearchParams(url.search).get('variant');
    } catch (e) {
      /* malformed URL — treat firstParam as a plain handle */
    }
  }

  handle = (handle || '').split('?')[0].split('#')[0].split('/').pop() || '';
  // Handles are lowercase alphanumerics and hyphens. Anything else is a bad
  // link or an injection attempt; both should resolve to "no pin". The handle
  // reaches a URL and a DOM query, so this guard is not cosmetic.
  if (!/^[a-z0-9-]+$/.test(handle)) handle = '';
  variant = (variant || '').replace(/[^0-9]/g, '') || null;

  return { handle, variant };
}

// ---- structural detection --------------------------------------------------

function isExcluded(el) {
  return el.closest(EXCLUDED) != null;
}

/**
 * Find the collection grid without knowing the theme.
 *
 * Walks up from every product link tallying, for each ancestor, how many
 * DISTINCT direct children lead to a product link. The grid is the ancestor
 * with the most — a 16-card grid scores 16 while <body> scores 1 or 2, because
 * all those links funnel through a single child on the way up.
 *
 * Ties are broken by depth: deepest wins. That matters when a grid is wrapped
 * in a container that has no other children, where wrapper and grid tie; the
 * grid is the one we want, because insertion has to land among the cards.
 *
 * Excluded regions are filtered first. Header mega-menus and cart drawers are
 * full of product links and would otherwise compete.
 */
function detectGrid() {
  if (cfg.gridSelector) {
    const explicit = document.querySelector(cfg.gridSelector);
    if (explicit) return explicit;
    warn('data-grid selector matched nothing: ' + cfg.gridSelector);
    return null;
  }

  const scope = cfg.containerSelector ? document.querySelector(cfg.containerSelector) : document;
  if (!scope) return null;

  const links = scope.querySelectorAll(PRODUCT_LINK);
  const tally = new Map();

  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    if (isExcluded(link)) continue;
    let node = link;
    while (node.parentElement && node !== document.body) {
      const parent = node.parentElement;
      let set = tally.get(parent);
      if (!set) {
        set = new Set();
        tally.set(parent, set);
      }
      set.add(node);
      node = parent;
    }
  }

  // Rank: most link-bearing children, then most uniform children, then
  // SHALLOWEST.
  //
  // Depth ascending is load-bearing and was originally the other way round.
  // On a two-card grid the real grid ties with structures INSIDE a card —
  // Horizon's <product-card> has two link-bearing children of its own (the
  // overlay anchor and the content div), so it scores 2 exactly like a
  // two-card grid does, and "deepest wins" handed back the inside of a card
  // as the grid. Nothing pinned, silently, but only on short collections.
  //
  // Shallowest is safe because a pure wrapper can never tie: a container whose
  // only child is the grid has ONE link-bearing child and is filtered out by
  // minCards long before it competes.
  let best = null;
  let bestCount = 0;
  let bestUniform = -1;
  let bestDepth = Infinity;
  tally.forEach((set, parent) => {
    const count = set.size;
    if (count < cfg.minCards) return;
    const uniform = uniformity(set);
    const depth = depthOf(parent);
    const better =
      count > bestCount ||
      (count === bestCount && uniform > bestUniform) ||
      (count === bestCount && uniform === bestUniform && depth < bestDepth);
    if (better) {
      best = parent;
      bestCount = count;
      bestUniform = uniform;
      bestDepth = depth;
    }
  });

  if (best) log('detected grid', best.tagName.toLowerCase() + '#' + best.id + '.' + best.className, bestCount + ' cards');
  return best;
}

function depthOf(el) {
  let d = 0;
  while ((el = el.parentElement)) d++;
  return d;
}

/**
 * Fraction of a candidate's link-bearing children that share the most common
 * tag name. A real grid is uniform — sixteen <li>, or sixteen <div> — while an
 * accidental candidate (a banner beside the grid, the inside of a card) mixes
 * tags. Used only to break count ties.
 */
function uniformity(set) {
  const tags = {};
  let top = 0;
  set.forEach((el) => {
    const t = el.tagName;
    tags[t] = (tags[t] || 0) + 1;
    if (tags[t] > top) top = tags[t];
  });
  return set.size ? top / set.size : 0;
}

/**
 * Cards are the grid's direct children that contain a product link.
 *
 * Deliberately not a class selector. Themes disagree completely on card class
 * names, but every one of them makes each card a direct child of the grid, and
 * every card links to its product. Filtering by "contains a product link" also
 * skips the non-card children themes park inside grids — Dawn's sessionStorage
 * <script>, sizing helpers, sold-out placeholders, ad slots.
 */
function getCards(grid) {
  if (!grid) return [];
  const out = [];
  const kids = grid.children;
  for (let i = 0; i < kids.length; i++) {
    if (cfg.cardSelector) {
      if (kids[i].matches(cfg.cardSelector)) out.push(kids[i]);
    } else if (kids[i].querySelector(PRODUCT_LINK) || kids[i].matches(PRODUCT_LINK)) {
      out.push(kids[i]);
    }
  }
  return out;
}

function firstCardOf(grid) {
  const cards = getCards(grid);
  return cards.length ? cards[0] : null;
}

/**
 * The element to hang the MutationObserver on.
 *
 * Not the grid: themes routinely replace the grid element wholesale on filter
 * (Dawn assigns innerHTML on the container above it), which would detach a
 * grid-scoped observer permanently after the first filter. The enclosing
 * Shopify section element survives every re-render a theme can do to its own
 * contents, and exists on every OS 2.0 theme.
 */
function observerRoot(grid) {
  if (cfg.containerSelector) {
    const explicit = document.querySelector(cfg.containerSelector);
    if (explicit) return explicit;
  }
  return grid.closest('[id^="shopify-section"]') || grid.parentElement || document.body;
}

// ---- handle matching -------------------------------------------------------

function linkMatchesHandle(href, handle) {
  if (!href) return false;
  const marker = '/products/' + handle;
  const at = href.indexOf(marker);
  if (at === -1) return false;
  // Prefix-collision guard: "shirt" must not match "shirt-blue". POD catalogs
  // are exactly where handles share prefixes.
  const next = href.charAt(at + marker.length);
  return next === '' || next === '?' || next === '#' || next === '/';
}

function cardMatchesHandle(card, handle) {
  const links = productLinksIn(card);
  for (let i = 0; i < links.length; i++) {
    if (linkMatchesHandle(links[i].getAttribute('href'), handle)) return true;
  }
  return false;
}

function findCardByHandle(grid, handle) {
  const cards = getCards(grid);
  for (let i = 0; i < cards.length; i++) if (cardMatchesHandle(cards[i], handle)) return cards[i];
  return null;
}

function findOtherCardWithHandle(grid, exclude, handle) {
  const cards = getCards(grid);
  for (let i = 0; i < cards.length; i++) {
    if (cards[i] !== exclude && cardMatchesHandle(cards[i], handle)) return cards[i];
  }
  return null;
}

// ---- visibility repair ------------------------------------------------------

/**
 * Make the pinned card unconditionally visible.
 *
 * Not cosmetic — without it the feature ships a blank first slot on any theme
 * with reveal-on-scroll animations, which is most of them.
 *
 * Themes gate card visibility on a class that an IntersectionObserver clears
 * when the element scrolls into view, and that observer is built once over the
 * elements present at load. Two failures follow. A card moved from grid
 * position 30 has never intersected, so it still carries the offscreen class
 * and computes to near-zero opacity — an invisible first card on a paid
 * landing. An injected card was never observed at all. Moving a node does not
 * re-run an IntersectionObserver callback, so nothing clears it later.
 *
 * Two passes, because neither alone is enough. The class list handles the
 * themes we know. The computed-style check handles the ones we do not: if the
 * card still resolves to near-invisible after the classes are stripped, inline
 * styles override whatever the theme is doing. Inline `!important`-free
 * declarations lose to an `!important` rule, which is the one case left
 * uncovered — the doctor script reports it if it happens.
 */
function makeVisible(card) {
  for (let i = 0; i < ANIMATION_CLASSES.length; i++) card.classList.remove(ANIMATION_CLASSES[i]);
  card.removeAttribute('data-cascade');
  card.removeAttribute('data-aos');
  card.style.removeProperty('--animation-order');

  if (!window.getComputedStyle) return;
  const cs = window.getComputedStyle(card);
  const invisible =
    parseFloat(cs.opacity) < 0.9 || cs.visibility === 'hidden' || cs.display === 'none';
  if (!invisible) return;

  card.style.setProperty('opacity', '1', 'important');
  card.style.setProperty('visibility', 'visible', 'important');
  card.style.setProperty('transform', 'none', 'important');
  card.style.setProperty('animation', 'none', 'important');
  if (cs.display === 'none') card.style.removeProperty('display');
  log('forced visibility on the pinned card');
}

// ---- state -----------------------------------------------------------------

/**
 * `failed` latches an unresolvable handle — without it every mutation frame
 * re-issues a request for a handle that will never resolve.
 * `inflight` covers the round trip BEFORE `failed` or `cachedInner` can latch:
 * a mutation arriving mid-request (a swatch, review or badge app is the usual
 * source) would otherwise re-enter pin() and fire request #2, #3, ... one per
 * animation frame until the first response lands.
 */
function createState() {
  return {
    pinning: false,
    cachedInner: null,
    cachedProduct: null,
    rafId: 0,
    variantImageSrc: null,
    failed: false,
    inflight: false,
  };
}

function init(parsed) {
  const state = createState();
  const grid = detectGrid();
  if (!grid) {
    warn('no collection grid found on this page — nothing to pin');
    return;
  }
  pin(parsed, state);
  observeReRenders(parsed, state, observerRoot(grid));
}

// ---- pinning ---------------------------------------------------------------

function pin(parsed, state) {
  // The pin retires the moment the shopper filters or sorts.
  //
  // Dawn-family filter code rebuilds the query string from form fields only
  // (new URLSearchParams(new FormData(form)) then history.pushState), so a
  // parameter that is not a form field is dropped. Re-reading it here makes
  // retirement explicit and self-cleaning: no event wiring, no observer
  // teardown, and it works the same on any theme that does the same thing.
  //
  // It is also the right product call. Without it, filtering to "Blue" returns
  // 8 products and renders 9 cards with the red one still pinned on top. And a
  // pin that outranks "Price: low to high" is not a pin — it is a broken sort.
  // The ad intent owns the landing; the shopper's first explicit choice ends it.
  //
  // A theme that MERGES params instead of rebuilding them keeps the pin alive
  // through filtering. Set data-mode="off" or handle it per-store if that turns
  // out to be true — the doctor script reports which behaviour a theme has.
  if (!new URLSearchParams(window.location.search).get(cfg.param)) return;

  const grid = detectGrid();
  if (!grid || state.pinning) return;

  const first = firstCardOf(grid);

  if (first && cardMatchesHandle(first, parsed.handle)) {
    // Already pinned. Pagination can later append the GENUINE server-rendered
    // card for this product from its real page, showing it twice. Prefer the
    // genuine card: swap it forward, drop ours.
    const duplicate = findOtherCardWithHandle(grid, first, parsed.handle);
    if (duplicate) {
      state.pinning = true;
      try {
        first.remove();
        makeVisible(duplicate);
        moveToFront(grid, duplicate);
        prioritizeImage(duplicate);
        if (parsed.variant) applyVariant(duplicate, parsed, state);
        log('replaced the pinned clone with the genuine card');
      } finally {
        state.pinning = false;
      }
      return;
    }
    makeVisible(first);
    if (parsed.variant) applyVariant(first, parsed, state);
    return;
  }

  state.pinning = true;
  try {
    const existing = findCardByHandle(grid, parsed.handle);

    if (existing) {
      makeVisible(existing);
      swapToFront(grid, existing);
      prioritizeImage(existing);
      if (parsed.variant) applyVariant(existing, parsed, state);
      log('pinned an existing card:', parsed.handle);
    } else if (state.cachedInner != null) {
      insertFetched(grid, state.cachedInner, parsed, state);
    } else if (state.cachedProduct && cfg.mode === 'clone') {
      insertClone(grid, parsed, state);
    } else if (!state.failed && !state.inflight) {
      acquireCard(grid, parsed, state);
    }
  } finally {
    state.pinning = false;
  }
}

function moveToFront(grid, card) {
  const displaced = firstCardOf(grid);
  if (displaced === card) return;
  grid.insertBefore(card, displaced || grid.firstChild);
}

/**
 * Swap with the current first card instead of splicing in front of it.
 *
 * Layout-shift control for the common case. insertBefore renumbers every slot
 * between 1 and k, so the browser re-lays-out that whole run of the grid. A
 * swap moves exactly two boxes and leaves slots 2..k-1 where they are.
 */
function swapToFront(grid, card) {
  const first = firstCardOf(grid);
  if (!first || first === card) return false;
  const after = card.nextSibling;
  grid.insertBefore(card, first);
  grid.insertBefore(first, after);
  return true;
}

/**
 * Mark the pinned card's image high priority.
 *
 * Themes render grid images lazily, which is right for a card at position 30
 * and wrong for this one: it is slot #1 on a paid landing, so it is the LCP
 * candidate, and it arrives late by construction — deferred script, plus a
 * round trip on the fetch path. Set before insertion so the request starts at
 * high priority the moment the node connects.
 */
function prioritizeImage(card) {
  const img = card.querySelector('img');
  if (!img) return;
  img.setAttribute('loading', 'eager');
  img.setAttribute('fetchpriority', 'high');
  // Themes that defer via data-src need the real src promoted, or the card
  // renders as a blank box until their lazyloader notices it.
  const deferred = img.getAttribute('data-src');
  if (deferred && !img.getAttribute('src')) img.setAttribute('src', deferred);
  const deferredSet = img.getAttribute('data-srcset');
  if (deferredSet && !img.getAttribute('srcset')) img.setAttribute('srcset', deferredSet);
}

// ---- acquiring a card the grid does not have -------------------------------

function acquireCard(grid, parsed, state) {
  // Marked BEFORE the request starts, not in .then() — a mutation frame landing
  // mid-round-trip has to see this and skip re-requesting. See createState().
  state.inflight = true;

  if (cfg.mode === 'clone') {
    fetchProductJson(parsed.handle)
      .then((product) => {
        state.cachedProduct = product;
        state.inflight = false;
        const liveGrid = detectGrid();
        if (liveGrid) pin(parsed, state);
      })
      .catch(failLatch(state));
    return;
  }

  const url =
    cfg.mode === 'section'
      ? shopifyRoot() + 'products/' + parsed.handle + '?section_id=' + encodeURIComponent(cfg.cardSection)
      : shopifyRoot() + 'products/' + parsed.handle + '?view=' + encodeURIComponent(cfg.view);

  fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(cfg.mode + ' request returned ' + res.status);
      return res.text();
    })
    .then((html) => {
      const inner = extractCardInner(html, parsed.handle);
      if (inner == null) throw new Error('no usable card markup for ' + parsed.handle);
      state.cachedInner = inner;
      state.inflight = false;
      const liveGrid = detectGrid();
      if (liveGrid) pin(parsed, state);
    })
    .catch(failLatch(state));
}

function failLatch(state) {
  return function (err) {
    state.failed = true;
    state.inflight = false;
    warn('could not pin product: ' + err.message);
  };
}

function fetchProductJson(handle) {
  return fetch(shopifyRoot() + 'products/' + handle + '.js').then((res) => {
    if (!res.ok) throw new Error('product.js ' + res.status);
    return res.json();
  });
}

/**
 * Pull the card's INNER markup out of a fetched response.
 *
 * Inner, not the whole element, because the wrapper is rebuilt from a live
 * sibling at insertion time — see buildCard(). That removes an entire class of
 * per-theme adaptation error: whoever writes the card snippet never has to
 * guess the grid item's tag or classes, and cannot get them wrong.
 *
 * A missing or unmatched card is treated as a failed lookup, never cached. An
 * unknown, unpublished or draft handle still answers HTTP 200 with a nil
 * product, and caching that render leaves the handle check unsatisfiable
 * forever — which is how a pin loop ends up re-inserting an empty clone on
 * every animation frame.
 */
function extractCardInner(html, handle) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const marker = doc.querySelector('#dm-curated-card');
  let host = marker;

  if (!host) {
    // section mode against a hand-written section, or a template without the
    // marker div: fall back to the body, minus anything non-visual.
    host = doc.body;
  }
  if (!host) return null;

  const junk = host.querySelectorAll('style, link[rel="stylesheet"], script, noscript');
  for (let i = 0; i < junk.length; i++) junk[i].remove();

  // If the response wrapped the card in its own grid item, unwrap one level so
  // we do not end up with a grid item inside a grid item.
  let content = host;
  const kids = elementChildren(host);
  if (kids.length === 1 && kids[0].querySelector(PRODUCT_LINK)) content = kids[0];

  const probe = document.createElement('div');
  probe.innerHTML = content.innerHTML;
  if (!cardMatchesHandle(probe, handle)) return null;

  return content.innerHTML;
}

function elementChildren(el) {
  const out = [];
  for (let i = 0; i < el.children.length; i++) out.push(el.children[i]);
  return out;
}

/**
 * Build a grid item for `innerHTML` whose shell matches the grid's real cards.
 *
 * cloneNode(false) on a sibling copies the tag and every attribute — classes,
 * inline custom properties, data hooks — without its contents. The fetched
 * markup then fills it. This is what makes the fetch path portable: the card
 * always sits in a wrapper the theme's own CSS already styles, whatever that
 * wrapper happens to be on this theme.
 *
 * Identity attributes are dropped afterwards. Copying an id or a product id
 * from the displaced card onto a different product is worse than having none.
 */
function buildCard(grid, innerHTML, handle) {
  const sibling = firstCardOf(grid);
  if (!sibling) return null;

  const shell = sibling.cloneNode(false);
  shell.removeAttribute('id');
  shell.removeAttribute('data-product-id');
  shell.removeAttribute('data-id');
  shell.removeAttribute('style');
  shell.setAttribute('data-dm-curated-pin', 'true');

  // On themes where the card element IS the product link — <a class="card"
  // href="/products/x"> with no wrapper — cloning the shell copies that href,
  // so the pinned card looked right and navigated to the DISPLACED product.
  // A wrong destination on a paid landing is the worst possible failure here,
  // and it is invisible until someone clicks.
  if (handle && shell.matches(PRODUCT_LINK)) {
    shell.setAttribute('href', shopifyRoot() + 'products/' + handle);
    shell.removeAttribute('aria-labelledby');
  }

  shell.innerHTML = innerHTML;
  return shell;
}

/** Every product link in a card, counting the card itself when it is one. */
function productLinksIn(card) {
  const links = [].slice.call(card.querySelectorAll(PRODUCT_LINK));
  if (card.matches && card.matches(PRODUCT_LINK)) links.unshift(card);
  return links;
}

function insertFetched(grid, innerHTML, parsed, state) {
  const node = buildCard(grid, innerHTML, parsed.handle);
  if (!node) return;
  adoptSiblingContent(grid, node);
  makeVisible(node);
  prioritizeImage(node);
  moveToFront(grid, node);
  if (parsed.variant) applyVariant(node, parsed, state);
  log('inserted a fetched card:', parsed.handle);
}

/**
 * Clone mode's insertion: copy a live sibling whole, then rewrite it to this
 * product. Rebuilt from the sibling on every call rather than cached, because
 * the grid it has to match may have been re-rendered since the last pass.
 */
function insertClone(grid, parsed, state) {
  const sibling = firstCardOf(grid);
  if (!sibling) return;
  const node = buildCard(grid, sibling.innerHTML, parsed.handle);
  if (!node) return;
  rewriteClone(node, state.cachedProduct, parsed.handle);
  makeVisible(node);
  prioritizeImage(node);
  moveToFront(grid, node);
  if (parsed.variant) applyVariant(node, parsed, state);
  log('inserted a cloned card:', parsed.handle);
}

// ---- clone mode -------------------------------------------------------------

function rewriteClone(card, product, handle) {
  const url = shopifyRoot() + 'products/' + handle;

  const strip = CLONE_STRIP.concat(cfg.stripSelector ? [cfg.stripSelector] : []);
  for (let i = 0; i < strip.length; i++) {
    const found = card.querySelectorAll(strip[i]);
    for (let j = 0; j < found.length; j++) found[j].remove();
  }

  const links = productLinksIn(card);
  for (let i = 0; i < links.length; i++) {
    links[i].setAttribute('href', url);
    links[i].removeAttribute('id');
    links[i].removeAttribute('aria-labelledby');
  }

  const img = card.querySelector('img');
  const imageSrc = imageSrcOf(product.featured_image);
  if (img && imageSrc) {
    // Rebuild srcset on the ladder the theme itself used, read off the card we
    // cloned. Inventing a ladder would give this one card different breakpoints
    // from every other card in the grid.
    applyImage(img, imageSrc, widthsFrom(img.getAttribute('srcset')), product.title);
  }

  rewriteTitle(card, product.title);

  applyClonePrice(card, product);

  return card;
}

/** Extract the width ladder from an existing srcset, e.g. "…165w, …360w". */
function widthsFrom(srcset) {
  const out = [];
  if (srcset) {
    const re = /(\d+)w/g;
    let m;
    while ((m = re.exec(srcset))) out.push(parseInt(m[1], 10));
  }
  return out.length ? out : [165, 360, 533, 720, 940, 1066];
}

function applyImage(img, src, widths, alt) {
  let base;
  try {
    base = new URL(src, window.location.href);
  } catch (e) {
    return;
  }
  // Query handling goes through the URL API, not string surgery: an existing
  // ?v=…&other=… must not leave a dangling & after v is stripped, nor gain a
  // second ? when width is appended.
  base.searchParams.delete('v');
  const mid = widths[Math.floor(widths.length / 2)] || 533;
  img.setAttribute('src', resize(base, mid));
  img.setAttribute('srcset', widths.map((w) => resize(base, w) + ' ' + w + 'w').join(', '));
  img.removeAttribute('data-src');
  img.removeAttribute('data-srcset');
  if (alt) img.setAttribute('alt', alt);
}

function resize(base, width) {
  const url = new URL(base.href);
  url.searchParams.set('width', String(width));
  return url.toString();
}

/**
 * `product.featured_image` is a plain URL STRING in /products/{handle}.js,
 * while `variants[i].featured_image` is an OBJECT with a .src. Mixing the two
 * up hands `new URL()` an object, which throws, gets swallowed by the guard in
 * applyImage, and leaves the clone showing the wrong product's photo with the
 * right product's name — the single most confusing way this can fail. Accept
 * both shapes and stop caring which one arrived.
 */
function imageSrcOf(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value.src || null;
}

/**
 * Replace the cloned card's title with the new product's.
 *
 * Located by structure, not by text: the title is the text of a link to the
 * product. Every theme does that, because the title has to be clickable.
 *
 * The original implementation searched for an element already containing the
 * NEW product's title — which is backwards, since the clone is still showing
 * the OLD product's title, so it matched nothing and silently left the wrong
 * name on the card. Nothing in the DOM tells us the old title, so anchoring on
 * "a link to this product that carries text" is what makes this work without
 * knowing anything about the theme.
 *
 * Links holding only an image, or only a price, are skipped.
 */
function rewriteTitle(card, title) {
  if (!title) return;
  const links = productLinksIn(card);

  for (let i = 0; i < links.length; i++) {
    const text = (links[i].textContent || '').trim();
    if (!text) continue;
    if (/^[\s$€£¥₹₽\d.,+\-–—]+$/.test(text)) continue; // price or numeric only
    const leaf = deepestTextLeaf(links[i]);
    (leaf || links[i]).textContent = title;
    return;
  }
}

/** First descendant with text and no element children; the element itself if none. */
function deepestTextLeaf(el) {
  const all = el.querySelectorAll('*');
  for (let i = 0; i < all.length; i++) {
    if (all[i].children.length) continue;
    if ((all[i].textContent || '').trim()) return all[i];
  }
  return el.children.length ? null : el;
}

/**
 * Rewrite the money in a cloned card by pattern, not by locale.
 *
 * The amount is re-rendered in the SAME format as the number already on the
 * card — separators, decimal places and surrounding text ("From $27.99") all
 * come from the clone. That sidesteps currency formatting entirely, which is
 * otherwise a per-shop setting (Shopify.money_format is not always present, and
 * markets can change it per request).
 *
 * Limitation, documented rather than hidden: compare-at prices are dropped when
 * the new product is not on sale, and a price RANGE collapses to the minimum.
 * Clone mode is the no-Liquid fallback; view mode renders real prices.
 */
function applyClonePrice(card, product) {
  const money = /\d[\d.,\s]*\d|\d/;
  const els = card.querySelectorAll('*');
  const onSale = product.compare_at_price && product.compare_at_price > product.price;

  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    if (el.children.length) continue;
    const text = el.textContent || '';
    if (!money.test(text)) continue;
    if (!/[$€£¥₹₽]|\d[.,]\d{2}/.test(text)) continue; // looks like money, not a count

    const token = text.match(money)[0];
    el.textContent = text.replace(money, formatLike(token, product.price));

    if (!onSale) {
      const strike = el.closest('s, del, [class*="compare"], [class*="regular"]');
      if (strike && strike !== card) strike.remove();
    }
  }
}

/** Render `cents` using the separators and decimal count of `sample`. */
function formatLike(sample, cents) {
  const decimalSep = /,\d{2}$/.test(sample) ? ',' : '.';
  const groupSep = decimalSep === ',' ? '.' : ',';
  const hasDecimals = new RegExp('[.,]\\d{2}$').test(sample);
  const amount = cents / 100;
  const fixed = hasDecimals ? amount.toFixed(2) : String(Math.round(amount));
  const parts = fixed.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
  return parts.length > 1 ? parts[0] + decimalSep + parts[1] : parts[0];
}

// ---- content parity ---------------------------------------------------------

/**
 * Strip blocks from a fetched card that NO real card in the grid has.
 *
 * Appearance and composition are different failure modes. A fetched card is
 * rendered from whatever settings its template or section resolves, which is
 * not necessarily what the merchant configured on the collection grid — so it
 * can arrive with a swatch row, a rating or a quick-add button that none of its
 * neighbours have. That happened on the first live install.
 *
 * The test is "no card in the grid has this", not "the first neighbour lacks
 * it". A product with only a default variant legitimately renders no swatch
 * row, so sampling one neighbour would delete a block the theme genuinely
 * asked for. Absent from EVERY card means a settings difference; present on
 * some means a per-product difference, and the clone keeps it.
 *
 * Safe in one direction only. If the real cards have something the fetched card
 * lacks, nothing here can invent it — that needs the card snippet corrected.
 */
function adoptSiblingContent(grid, card) {
  const siblings = getCards(grid).filter((c) => c !== card);
  if (!siblings.length) return;

  for (let i = 0; i < OPTIONAL_BLOCKS.length; i++) {
    const selector = OPTIONAL_BLOCKS[i];
    const mine = card.querySelectorAll(selector);
    if (!mine.length) continue;

    let present = false;
    for (let s = 0; s < siblings.length; s++) {
      if (siblings[s].querySelector(selector)) {
        present = true;
        break;
      }
    }
    if (present) continue;

    for (let m = 0; m < mine.length; m++) mine[m].remove();
    log('removed a block absent from every real card:', selector);
  }
}

// ---- variant deep-link + image swap ----------------------------------------

function applyVariant(card, parsed, state) {
  if (!parsed.variant) return;
  setVariantOnLinks(card, parsed.handle, parsed.variant);
  if (!cfg.swapImage) return;
  if (state.variantImageSrc) {
    swapImage(card, state.variantImageSrc);
  } else {
    resolveVariantImage(card, parsed, state);
  }
}

/**
 * Point every anchor in the card that links to THIS product at the chosen
 * variant.
 *
 * Cards commonly carry more than one link to their own product — a title link
 * and a separate image link at minimum, plus whatever quick-add and swatch
 * widgets add. Rewriting only the first would leave the image, which is what
 * shoppers actually aim at, going to the default variant. Anchors are matched
 * by handle, so app-injected links to OTHER products stay untouched.
 */
function setVariantOnLinks(card, handle, variantId) {
  const links = productLinksIn(card);
  for (let i = 0; i < links.length; i++) {
    const href = links[i].getAttribute('href');
    if (!href || !linkMatchesHandle(href, handle)) continue;
    links[i].setAttribute('href', withVariantParam(href, variantId));
  }
}

function withVariantParam(href, variantId) {
  try {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('variant', variantId);
    return url.pathname + url.search + url.hash;
  } catch (e) {
    return href;
  }
}

function resolveVariantImage(card, parsed, state) {
  const use = (product) => {
    const idNum = parseInt(parsed.variant, 10);
    let variant = null;
    for (let i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === idNum) {
        variant = product.variants[i];
        break;
      }
    }
    if (!variant || !variant.featured_image || !variant.featured_image.src) return;
    state.variantImageSrc = variant.featured_image.src;
    swapImage(card, state.variantImageSrc);
  };

  if (state.cachedProduct) {
    use(state.cachedProduct);
    return;
  }
  fetchProductJson(parsed.handle)
    .then((product) => {
      state.cachedProduct = product;
      use(product);
    })
    .catch(() => {
      /* non-fatal: the card still works, it just shows the default image */
    });
}

/**
 * Swap the card's primary image only — never hover or carousel slides, which
 * belong to the theme's own gallery component and have their own state.
 */
function swapImage(card, imageSrc) {
  if (card.dataset.dmVariantSwapped === imageSrc) return;
  const img = card.querySelector('img');
  if (!img) return;
  applyImage(img, imageSrc, widthsFrom(img.getAttribute('srcset')), null);
  card.dataset.dmVariantSwapped = imageSrc;
}

// ---- re-render survival ------------------------------------------------------

/**
 * Hold the pin through every way a theme can rewrite the grid: filter and sort
 * re-renders, "load more" and infinite scroll, quick-add morphs, and third-party
 * review, badge and swatch apps rewriting individual cards.
 *
 * Coalesced to one pin() per animation frame. Even when already pinned, pin()
 * re-scans once — that is what catches a pagination duplicate, and what wins the
 * race against a swatch app rewriting the card link back to its default variant.
 * O(cards) per frame, not per mutation.
 */
function observeReRenders(parsed, state, root) {
  if (!root || typeof MutationObserver === 'undefined') return;
  const observer = new MutationObserver(() => {
    if (state.pinning || state.rafId) return;
    state.rafId = window.requestAnimationFrame(() => {
      state.rafId = 0;
      pin(parsed, state);
    });
  });
  observer.observe(root, { childList: true, subtree: true });
  log('observing', root.tagName.toLowerCase() + (root.id ? '#' + root.id : ''));
}
